import { useCallback } from 'react';

import {
  type UploadAnalyticsCommonParams,
  useTrackUploadPartAborted,
  useTrackUploadPartCompleted,
  useTrackUploadPartError,
  useTrackUploadPartStarted,
} from '@air/analytics';
import { type Upload } from '@air/redux-uploader';
import { isAbortError, type UploadFileChunkParams } from '@air/upload-utils';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type UploadTaskParams } from '~/components/Upload/hooks/types';
import {
  getUploadPartAbortedAnalyticsParams,
  getUploadPartCompletedAnalyticsParams,
  getUploadPartErrorAnalyticsParams,
  getUploadPartStartedAnalyticsParams,
} from '~/components/Upload/utils/uploadAnalytics';

export const useLargeUploadPartTrackingHandlers = () => {
  const { trackUploadPartAborted } = useTrackUploadPartAborted();
  const { trackUploadPartCompleted } = useTrackUploadPartCompleted();
  const { trackUploadPartError } = useTrackUploadPartError();
  const { trackUploadPartStarted } = useTrackUploadPartStarted();

  const getPartTrackingHandlers = useCallback(
    ({
      getAnalyticsParams,
      onError,
    }: {
      getAnalyticsParams: (upload: Upload) => UploadAnalyticsCommonParams;
      onError: UploadTaskParams['onError'];
    }): Pick<UploadFileChunkParams, 'onPartCompleted' | 'onPartError' | 'onPartStarted' | 'onUploadFailed'> => ({
      onPartCompleted: ({
        isOnline,
        maxRetryCount,
        partNumber,
        partUploadDurationMs,
        retryCount,
        upload,
        uploadUrlType,
      }) => {
        try {
          const analyticsParams = getAnalyticsParams(upload);

          trackUploadPartCompleted(
            getUploadPartCompletedAnalyticsParams({
              commonParams: analyticsParams,
              isOnline,
              maxRetryCount,
              partNumber,
              partUploadDurationMs,
              retryCount,
              upload,
              uploadUrlType,
            }),
          );
        } catch (analyticsError) {
          reportErrorToBugsnag({
            error: analyticsError,
            context: 'Failed to track upload part completed analytics',
          });
        }
      },
      onPartError: ({
        abortReason,
        error,
        isOnline,
        isRetryable,
        maxRetryCount,
        partNumber,
        retryCount,
        upload,
        uploadUrlType,
      }) => {
        if (!isAbortError(error)) {
          reportErrorToBugsnag({
            error,
            context: 'Failed to upload large chunk',
            metadata: {
              data: {
                batchId: upload.batchId,
                clipId: upload.clipId || undefined,
                id: upload.id,
                partNumber,
              },
            },
          });
        }

        try {
          const analyticsParams = getAnalyticsParams(upload);

          if (isAbortError(error)) {
            trackUploadPartAborted(
              getUploadPartAbortedAnalyticsParams({
                abortReason,
                commonParams: analyticsParams,
                error,
                isOnline,
                maxRetryCount,
                partNumber,
                retryCount,
                upload,
              }),
            );
          } else {
            trackUploadPartError(
              getUploadPartErrorAnalyticsParams({
                commonParams: analyticsParams,
                error,
                isOnline,
                isRetryable,
                maxRetryCount,
                partNumber,
                retryCount,
                upload,
                uploadUrlType,
              }),
            );
          }
        } catch (analyticsError) {
          reportErrorToBugsnag({
            error: analyticsError,
            context: 'Failed to track upload part analytics',
          });
        }
      },
      onPartStarted: ({ isOnline, maxRetryCount, partNumber, retryCount, upload, uploadUrlType }) => {
        try {
          const analyticsParams = getAnalyticsParams(upload);

          trackUploadPartStarted(
            getUploadPartStartedAnalyticsParams({
              commonParams: analyticsParams,
              isOnline,
              maxRetryCount,
              partNumber,
              retryCount,
              upload,
              uploadUrlType,
            }),
          );
        } catch (analyticsError) {
          reportErrorToBugsnag({
            error: analyticsError,
            context: 'Failed to track upload part started analytics',
          });
        }
      },
      onUploadFailed: ({ error, upload, uploadUrlType }) => {
        onError({ error, failedStage: 'part_upload', upload, uploadUrlType });
      },
    }),
    [trackUploadPartAborted, trackUploadPartCompleted, trackUploadPartError, trackUploadPartStarted],
  );

  return {
    getPartTrackingHandlers,
  };
};
